import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DollarSign, Search, RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TutorEarningsCard from './TutorEarningsCard';
import PaymentRowActions from './PaymentRowActions';
import { downloadCsv } from '@/lib/csvExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Payment Management Panel — Paystack LIVE
 *
 * Renders real Paystack payment records sourced from
 * GET /api/payments/admin/list. Read-only view. No refunds, no commission
 * controls (those are deliberately out of scope per the current phase rules).
 */

const PAYMENT_TYPE_LABELS = {
  trial_session: 'Trial Session',
  single_session: '1:1 Session',
  intensive_package: 'Intensive Package',
  donation: 'Donation',
};

const PROVIDER_BADGE = {
  paystack: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  paypal: 'bg-blue-100 text-blue-800 border-blue-200',
};

const STATUS_BADGE = {
  success: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  abandoned: 'bg-gray-100 text-gray-700 border-gray-200',
};

const REFUND_BADGE = {
  none: null, // render nothing if no refund
  requested: 'bg-amber-100 text-amber-900 border-amber-200',
  partial: 'bg-orange-100 text-orange-900 border-orange-200',
  full: 'bg-red-100 text-red-900 border-red-200',
  rejected: 'bg-gray-100 text-gray-700 border-gray-200',
};

const REFUND_LABEL = {
  none: '—',
  requested: 'Requested',
  partial: 'Partial',
  full: 'Full',
  rejected: 'Rejected',
};

const RefundBadge = ({ status }) => {
  if (!status || status === 'none') return <span className="text-gray-400">—</span>;
  const cls = REFUND_BADGE[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <Badge
      className={`${cls} border text-[11px] font-medium`}
      data-testid={`refund-badge-${status}`}
    >
      {REFUND_LABEL[status] || status}
    </Badge>
  );
};

const formatMoney = (amount, currency) => {
  if (typeof amount !== 'number') return '—';
  return `${currency === 'USD' ? '$' : ''}${amount.toFixed(2)} ${currency || ''}`.trim();
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const cls = STATUS_BADGE[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  const label = (status || 'unknown').toUpperCase();
  return (
    <Badge
      className={`${cls} border font-medium`}
      data-testid={`payment-status-${status || 'unknown'}`}
    >
      {label}
    </Badge>
  );
};

const PaymentManagementPanel = ({ payments = [], onRefresh }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refundFilter, setRefundFilter] = useState('all');
  // Commission split is driven by backend env. Defaults mirror the canonical
  // 70/30 rule so the UI works even if the config fetch is in flight.
  const [commission, setCommission] = useState({
    tutorPercent: 70,
    platformPercent: 30,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/config`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const t = Number(data?.tutor_commission_percent);
        const p = Number(data?.platform_commission_percent);
        if (Number.isFinite(t) && Number.isFinite(p) && t > 0 && p > 0) {
          setCommission({ tutorPercent: t, platformPercent: p });
        }
      } catch (_) {
        /* fall back to defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Helper — returns the tutor/platform share for a payment. Prefers the values
  // persisted on the record (set at initialize-time against the commission rule
  // active then). Falls back to a live calculation so records from before this
  // field was added still display something sensible.
  const splitFor = (p) => {
    const amount = Number(p.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { tutor: 0, platform: 0 };
    const tutor =
      typeof p.tutor_amount === 'number'
        ? p.tutor_amount
        : (amount * commission.tutorPercent) / 100;
    const platform =
      typeof p.platform_amount === 'number'
        ? p.platform_amount
        : (amount * commission.platformPercent) / 100;
    return { tutor, platform };
  };

  // Newest first — backend already sorts, but we re-sort defensively in case
  // the order is ever changed.
  const sorted = useMemo(
    () =>
      [...payments].sort((a, b) =>
        (b.created_at || '').localeCompare(a.created_at || '')
      ),
    [payments]
  );

  const filtered = useMemo(() => {
    return sorted.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (p.student_name || '').toLowerCase().includes(q) ||
        (p.student_email || '').toLowerCase().includes(q) ||
        (p.paystack_reference || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesRefund =
        refundFilter === 'all' ||
        (refundFilter === 'any' && p.refund_status && p.refund_status !== 'none') ||
        p.refund_status === refundFilter;
      return matchesSearch && matchesStatus && matchesRefund;
    });
  }, [sorted, search, statusFilter, refundFilter]);

  const totalSuccess = useMemo(
    () =>
      sorted
        .filter((p) => p.status === 'success')
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [sorted]
  );

  const successCount = sorted.filter((p) => p.status === 'success').length;

  // ---------- CSV export ----------
  const exportCsv = () => {
    if (filtered.length === 0) return;
    const { tutorPercent, platformPercent } = commission;
    const columns = [
      { key: 'student_name', header: 'Payer' },
      { key: 'student_email', header: 'Email' },
      { key: 'amount', header: 'Amount', format: (v) => (typeof v === 'number' ? v.toFixed(2) : v) },
      { key: 'currency', header: 'Currency', format: (v) => v || 'USD' },
      { key: 'status', header: 'Status' },
      {
        key: 'tutor_amount',
        header: `Tutor share (${tutorPercent}%)`,
        format: (v, row) => {
          if (row.status !== 'success') return '';
          const amt =
            typeof v === 'number'
              ? v
              : (Number(row.amount) || 0) * (tutorPercent / 100);
          return amt.toFixed(2);
        },
      },
      {
        key: 'platform_amount',
        header: `Company share (${platformPercent}%)`,
        format: (v, row) => {
          if (row.status !== 'success') return '';
          const amt =
            typeof v === 'number'
              ? v
              : (Number(row.amount) || 0) * (platformPercent / 100);
          return amt.toFixed(2);
        },
      },
      { key: 'payment_type', header: 'Type' },
      { key: 'provider', header: 'Provider', format: (v) => v || 'paystack' },
      { key: 'paystack_reference', header: 'Paystack reference' },
      { key: 'refund_status', header: 'Refund status', format: (v) => v || 'none' },
      { key: 'refund_notes', header: 'Refund notes' },
      { key: 'notes', header: 'Notes' },
      { key: 'created_at', header: 'Created (UTC ISO)' },
      { key: 'verified_at', header: 'Verified (UTC ISO)' },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`payments-${stamp}.csv`, filtered, columns);
  };

  const totals = useMemo(() => {
    return sorted
      .filter((p) => p.status === 'success')
      .reduce(
        (acc, p) => {
          const { tutor, platform } = splitFor(p);
          acc.tutor += tutor;
          acc.platform += platform;
          return acc;
        },
        { tutor: 0, platform: 0 }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, commission]);

  return (
    <div className="space-y-6">
      <TutorEarningsCard />
      <Card data-testid="payments-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Payments ({sorted.length})</CardTitle>
            {sorted.length > 0 && (
              <div className="mt-1 space-y-0.5">
                <p className="text-sm text-gray-500">
                  {successCount} successful · ${totalSuccess.toFixed(2)} USD
                  collected (lifetime)
                </p>
                {successCount > 0 && (
                  <p
                    className="text-xs text-gray-500"
                    data-testid="payments-commission-totals"
                  >
                    Projected tutor share:{' '}
                    <span className="font-semibold text-gray-700">
                      ${totals.tutor.toFixed(2)} USD
                    </span>{' '}
                    · Company retained:{' '}
                    <span className="font-semibold text-gray-700">
                      ${totals.platform.toFixed(2)} USD
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              className="bg-indigo-50 text-indigo-800 border border-indigo-200"
              data-testid="payments-commission-rule"
            >
              Commission {commission.tutorPercent}/{commission.platformPercent}{' '}
              · view-only
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
              Paystack · LIVE
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={filtered.length === 0}
              className="gap-1.5"
              data-testid="payments-export-csv"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV ({filtered.length})
            </Button>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="gap-1.5"
                data-testid="payments-refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        {sorted.length > 0 && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or reference…"
                className="pl-8"
                data-testid="payments-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md p-2 bg-white text-sm"
              data-testid="payments-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="abandoned">Abandoned</option>
            </select>
            <select
              value={refundFilter}
              onChange={(e) => setRefundFilter(e.target.value)}
              className="border border-gray-300 rounded-md p-2 bg-white text-sm"
              data-testid="payments-refund-filter"
            >
              <option value="all">All Refunds</option>
              <option value="any">Any refund activity</option>
              <option value="requested">Requested</option>
              <option value="partial">Partial</option>
              <option value="full">Full</option>
              <option value="rejected">Rejected</option>
              <option value="none">No refund</option>
            </select>
          </div>
        )}

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="payments-empty">
            <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {sorted.length === 0
                ? 'No payment records yet'
                : 'No payments match the current filters.'}
            </p>
            {sorted.length === 0 && (
              <p className="text-sm text-gray-400 mt-1">
                Real Paystack transactions will appear here as soon as your
                first customer pays.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table
              className="w-full text-sm"
              data-testid="payments-table"
            >
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 tracking-wide">
                <tr>
                  <th className="px-3 py-2 font-semibold">Payer</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Amount</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">
                    Tutor ({commission.tutorPercent}%)
                  </th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">
                    Company ({commission.platformPercent}%)
                  </th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Provider</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Refund</th>
                  <th className="px-3 py-2 font-semibold">Reference</th>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p) => {
                  const { tutor, platform } = splitFor(p);
                  const isSuccess = p.status === 'success';
                  const currency = p.currency || 'USD';
                  return (
                    <tr
                      key={p.id || p.paystack_reference}
                      className="hover:bg-gray-50"
                      data-testid={`payment-row-${p.paystack_reference}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {p.student_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {p.student_email || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap">
                        {formatMoney(p.amount, currency)}
                      </td>
                      <td
                        className="px-3 py-2 whitespace-nowrap"
                        data-testid={`payment-tutor-${p.paystack_reference}`}
                      >
                        {isSuccess ? (
                          <span className="text-emerald-700 font-medium">
                            {formatMoney(tutor, currency)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 whitespace-nowrap"
                        data-testid={`payment-company-${p.paystack_reference}`}
                      >
                        {isSuccess ? (
                          <span className="text-indigo-700 font-medium">
                            {formatMoney(platform, currency)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          className={`${PROVIDER_BADGE[p.provider || 'paystack'] || 'bg-gray-100 text-gray-700 border-gray-200'} border text-[11px] font-medium capitalize`}
                          data-testid={`payment-provider-${p.provider || 'paystack'}`}
                        >
                          {p.provider || 'paystack'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-3 py-2">
                        <RefundBadge status={p.refund_status} />
                      </td>
                      <td className="px-3 py-2">
                        <code className="font-mono text-xs text-gray-600">
                          {p.paystack_reference || '—'}
                        </code>
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {formatDate(p.created_at)}
                        {p.notes && (
                          <div
                            className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1 inline-block max-w-[220px] truncate"
                            title={p.notes}
                            data-testid={`payment-notes-preview-${p.paystack_reference}`}
                          >
                            📝 {p.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <PaymentRowActions
                          payment={p}
                          onRefresh={onRefresh}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
};

export default PaymentManagementPanel;
